// The Sentry-side half of "is this event worth a person's attention?" — and
// the one place that decides which issue it belongs to.
//
// Lives here, not inline in main.tsx, for one reason: main.tsx cannot be
// imported by a unit test (it boots the app), and an untested filter is a
// filter that silently eats real errors. sentryBeforeSend.test.ts pins every
// branch below.
//
// ── One list, not two ────────────────────────────────────────────────────────
// This replaces the `ignoreErrors` array that used to hand-mirror part of
// errorReporter's SWALLOWED_PATTERNS — the quality playbook literally said
// "both filters need the same regex". That mirror is why the transport
// rate-card gap took two commits four weeks apart (one list, then the other,
// four weeks later), and it never carried DB_VALIDATION_PATTERNS at all, so
// every operator-fixable DB backstop ("Pallet underweight for …", "no rate
// effective on …") paged the team as an error even though the reporter had
// already decided it should not.
//
// `isSwallowed` is now the ONLY list. Sentry's own global handlers capture
// unhandled errors in PARALLEL with reportError — they never pass through it —
// so this hook is what makes the reporter's decision apply to them too.

import type { ErrorEvent, EventHint } from '@sentry/react'
import { isSwallowed } from '@/lib/errorReporter'
import { dbFingerprint } from '@/lib/messageFamily'

// Noise that ONLY Sentry can see, so it cannot live in errorReporter's lists —
// mirroring it there would be a dead pattern nothing can reach. Each of these
// is produced by the browser or by Sentry's own instrumentation and never
// passes through reportError.
const SENTRY_ONLY_NOISE: RegExp[] = [
  // Browser layout-loop warning, surfaced as a window error. No app code
  // throws it, so reportError never sees it.
  /ResizeObserver loop (?:limit exceeded|completed with undelivered notifications)/i,
  // Sentry's OWN synthetic message for a promise rejected with a non-Error
  // value. The text does not exist outside Sentry.
  /Non-Error promise rejection captured/i,
  // CSP-eval violations from older Safari (iOS 15-) hitting Replay's runtime
  // code-gen: raised by the browser against Sentry's own bundle. Old cached
  // builds from before we added 'unsafe-eval' still fire these.
  /Refused to evaluate a string as JavaScript/i,
]

// DELIBERATELY NOT HERE, carried over from the `ignoreErrors` list this
// replaces (note dated 2026-06-11): the chunk-load failure classes
// ('Importing a module script failed', 'Failed to fetch dynamically imported
// module', 'Loading chunk N failed'). They were silenced once on the theory
// that lazyWithReload's auto-reload made them benign — the root-cause
// investigation then showed they were the #1 user-facing breakage (deploys
// hot-swapping the SW + purging chunks) and the silencing had hidden it for a
// month. With prompt-mode SW updates they should be RARE; if they spike, the
// update pipeline has regressed, and that signal is the point.

/**
 * The text an event is ABOUT: the exception's own value when there is one,
 * else the captured message. Deliberately NOT prefixed with the exception
 * type — a `fn_name:` RAISE prefix has to stay at the front of the string for
 * messageFamily to hold it out of normalisation.
 */
export function sentryEventText(event: ErrorEvent): string {
  const ex = event.exception?.values?.[0]
  if (typeof ex?.value === 'string' && ex.value) return ex.value
  return typeof event.message === 'string' ? event.message : ''
}

/**
 * Is this a real deployed app, or a dev server / e2e harness on someone's
 * laptop?
 *
 * Measured (client-issues research, 2026-09-11): the gitignored local `.env`
 * carries the PRODUCTION DSN and the Playwright configs overrode the Supabase
 * vars but not VITE_SENTRY_DSN, so local sweeps posted "e2e injected failure"
 * into the prod project — 101 events in Sentry's Aug 22–29 weekly report, 102
 * in Aug 29–Sep 5, about half of two weekly digests, tagged
 * environment=development, url=http://localhost:4319/. Worse than the noise:
 * replay is sampled at 1.0 on error and the 50-replay Free allowance is
 * org-wide, so a local sweep spends the replay the client-reported issue later
 * in the month needed.
 *
 * PROD alone is not enough: playwright.sw.config.ts runs a real `vite build`
 * (PROD === true) and serves it from localhost, so the hostname test is the
 * half that catches it. The Playwright configs now blank the DSN as well; this
 * is the layer that holds when someone adds a fourth config and forgets.
 */
export function sentryEnabled(isProd: boolean, hostname: string | undefined): boolean {
  if (!isProd) return false
  if (!hostname) return false
  return !/^(?:localhost$|127\.|0\.0\.0\.0$|\[?::1\]?$|.*\.local$)/i.test(hostname)
}

/**
 * Drop what the reporter would drop; group a database error by what it IS.
 *
 * Returns null to discard the event (a discarded event never flushes a
 * replay, which is where the quota burn actually stops), otherwise the event,
 * with `fingerprint` set when the error is PostgREST/RAISE-shaped.
 */
export function sentryBeforeSend(event: ErrorEvent, hint?: EventHint): ErrorEvent | null {
  try {
    const text = sentryEventText(event)

    // A ValidationError that crossed a realm boundary (or was re-thrown by a
    // wrapper) loses `instanceof`; the exception TYPE survives on the event.
    if (event.exception?.values?.some(v => v.type === 'ValidationError')) return null

    // The one list.
    if (isSwallowed(hint?.originalException ?? text)) return null

    if (SENTRY_ONLY_NOISE.some(re => re.test(text))) return null

    // Group a database refusal/defect by what it IS, not by the minified stack
    // it arrived on. Without this, one defect mints a new issue per deploy
    // (F329 -76/-77/-78 within six hours) and one parametrised RAISE mints one
    // per parameter (seven "Invoice number X is already used" issues, ten
    // digest "new groups"). Non-database errors keep Sentry's default
    // grouping — a real stack discriminates better than a sentence.
    //
    // `db_code` is set by reportError (errorReporter.ts) because a
    // PostgrestError is a plain object: it goes to Sentry via captureMessage,
    // where `hint.originalException` is empty and the code would otherwise be
    // invisible here.
    const codeTag = typeof event.tags?.db_code === 'string' ? event.tags.db_code : null
    const fp = dbFingerprint(codeTag ? { code: codeTag } : hint?.originalException, text)
    if (fp) event.fingerprint = fp
  } catch {
    // A filter that throws must never eat the event it was filtering.
  }
  return event
}
